import { AxiosResponse } from 'axios';
import {
  ChannelContributors,
  ChannelMembersResponse,
  ChannelStat,
  ChannelStatsResponse,
  ChannelTopContributorsResponse,
} from '../../models/channels/types';
import ChannelService from '../../services/channel';
import { Follower, mapMember } from './channel-member';
import { toChannelStat } from './use-channel-stats';
import { useEffect, useState } from 'react';

const useChannelDetails = (channelId: string) => {
  const [fetching, setFetching] = useState(true);

  const [channelStats, setChannelStats] = useState<ChannelStat>();
  const [channelContributors, setChannelContributors] =
    useState<ChannelContributors>([]);
  const [channelFollowers, setChannelFollowers] = useState<Follower[]>([]);

  useEffect(() => {
    const channelService = new ChannelService(channelId);
    // The sidebar stays mounted when the channel changes. Responses for the
    // previous channel are ignored, and each section starts empty so a failed
    // request can't leave the previous channel's data on screen.
    let cancelled = false;

    // Each request fills its own section and fails on its own, so a rejected
    // members or contributors call still leaves the stats on screen.
    const settle = <T,>(
      request: Promise<T>,
      onSuccess: (response: T) => void,
      label: string
    ) =>
      request
        .then((response) => {
          if (!cancelled) onSuccess(response);
        })
        .catch((err: unknown) => {
          console.error(err, `Error fetching channel ${label}`);
        });

    const fetchChannelStats = () => {
      setFetching(true);
      setChannelStats(undefined);
      setChannelContributors([]);
      setChannelFollowers([]);

      Promise.all([
        settle(
          channelService.fetchStats(),
          (stats: AxiosResponse<ChannelStatsResponse>) => {
            setChannelStats(toChannelStat(stats.data.stats));
          },
          'stats'
        ),
        settle(
          channelService.fetchContributors(),
          (contributors: AxiosResponse<ChannelTopContributorsResponse>) => {
            setChannelContributors(
              contributors.data.top_contributors.filter((c) => c.display_name)
            );
          },
          'contributors'
        ),
        settle(
          channelService.fetchMembers(),
          (members: AxiosResponse<ChannelMembersResponse>) => {
            setChannelFollowers(members.data.data.map(mapMember));
          },
          'members'
        ),
      ]).finally(() => {
        if (!cancelled) setFetching(false);
      });
    };

    fetchChannelStats();

    return () => {
      cancelled = true;
    };
  }, [channelId]);

  return {
    fetching,
    channelContributors,
    channelFollowers,
    channelStats,
  };
};

export default useChannelDetails;
